import axios from "axios";
import { ExportReportResponse } from "../models/serviceModels/ExportReportResultResponse";
import {
    ReportDefinition,
    ReportLibraryData,
    ReportMetadataLibrary,
} from "../models/pageModels/ReportLibrary";
import { 
    ReportGroupDashboard, 
    GroupReportWithSummaries 
} from "../models/pageModels/ReportDashboard";
import { GroupFiltersResponse, ApplyFiltersRequest, ApplyFiltersResponse } from "../models/pageModels/GroupFilters";
import PagedData from "../models/serviceModels/PagedData";
import RunReportV2Request from "../models/serviceModels/RunReportV2Request";
import GetReportResultsRequest from "../models/serviceModels/GetReportResultsRequest";
import RunReportV2Response from "../models/serviceModels/RunReportV2Response";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

/**
 * CORE REPORT FUNCTIONS
 */

const runReportV2 = async (request: RunReportV2Request, mode?: string): Promise<RunReportV2Response> => {
    const url = `${baseUrl}/reports/v2/run?mode=${mode ?? 'new'}`;    
    const result = await axios.post(url, request);
    return result.data;
};

const getReportResultsV2 = async <T>(request: GetReportResultsRequest): Promise<PagedData<T>> => {
    const params = new URLSearchParams({
        skip: request.skip.toString(),
        take: request.take.toString(),
        sortBy: request.sortBy,
        eventDirection: request.sortDirection,
        filter: request.filter ?? ''
    });

    const result = await axios.get(`${baseUrl}/reports/v2/${request.reportId}/results?${params.toString()}`);
    return result.data;
};

const getReportSummaryV2 = async <T>(reportId: string, signal?: AbortSignal): Promise<T> => {
    const result = await axios.get(`${baseUrl}/reports/v2/${reportId}/summary`, { signal });
    return result.data;
};

const getReportDefinition = async (reportId: string, signal?: AbortSignal): Promise<ReportDefinition> => {
    const result = await axios.get(`${baseUrl}/reports/v2/${reportId}`, { signal });
    return result.data;
}

const getReportsMetadata = async (): Promise<ReportMetadataLibrary> => {
    const result = await axios.get(`${baseUrl}/reports/v2/metadata`);
    return result.data;
};

const getReportStalenessStatus = async (reportId: string): Promise<{isStale: boolean; parentReportId?: string}> => {
    const result = await axios.get(`${baseUrl}/reports/v2/${reportId}/check-staleness`);
    return result.data;
};

const exportReport = async ({reportId}: {reportId: string}): Promise<ExportReportResponse> => {
    const result = await axios.post(`${baseUrl}/reports/v2/${reportId}/export`);
    return result.data;
};

const getUserReports = async (): Promise<ReportLibraryData> => {
    const result = await axios.get(`${baseUrl}/reports/v2/user`);
    return result.data;
};

const updateReportMetadata = async ({
    reportId,
    name,
    isBookmarked,
}: {
    reportId: string;
    name?: string;
    isBookmarked?: boolean;
}): Promise<void> => {
    await axios.post(`${baseUrl}/reports/v2/${reportId}/metadata`, {
        name,
        isBookmarked,
    });
};

const checkReportStale = async (
    reportId: string,
): Promise<{ isStale: boolean; parentReportId: string }> => {
    const result = await axios.post(`${baseUrl}/reports/v2/${reportId}/check-stale`);
    return result.data;
};

/**
 * UNIFIED REPORT ACTION HANDLER
 */
const executeReportAction = async (
    reportId: string, 
    action: string, 
    payload?: any
): Promise<{id: string, updated: true}> => {
    const body =
        typeof payload === "string"
            ? { action, name: payload }
            : {
                  action,
                  ...(payload ?? {}),
              };

    const result = await axios.post(`${baseUrl}/reports/v2/${reportId}/actions`, body);
    return result.data;
};

/**
 * REPORT GROUPING
 */

const getAllReportGroups = async (): Promise<{ groupId: string; groupName: string; reportCount: number }[]> => {
    const result = await axios.get(`${baseUrl}/reports/v2/groups`);
    return result.data;
};

const newReportGroup = async ({
    groupName,
    reportIds,
}: {
    groupName: string;
    reportIds: string[];
}): Promise<{ groupId: string; groupName: string }> => {
    const result = await axios.post(`${baseUrl}/reports/v2/groups`, {
        groupName,
        reportIds,
    });
    return result.data;
};

const deleteReportGroup = async (groupId: string) => {
    return await axios.delete(`${baseUrl}/reports/v2/groups/${groupId}`);
};

const renameReportGroup = async (groupId: string, newName: string) => {
    return await axios.put(`${baseUrl}/reports/v2/groups/${groupId}`, {
        newName,
    });
};

const assignReportsToGroup = async ({
    groupId,
    reportIds,
}: {
    groupId: string | null;
    reportIds: string[];
}): Promise<void> => {
    await axios.post(`${baseUrl}/reports/v2/groups/assign`, {
        groupId,
        reportIds,
    });
};

const executeGroupAction = async (action: string, payload?: any): Promise<any> => {
    const result = await axios.post(`${baseUrl}/reports/v2/groups/actions`, {
        action,
        ...payload
    });
    return result.data;
};

/**
 * GLOBAL FILTERS
 */

const getGroupFilters = async (groupId: string): Promise<GroupFiltersResponse> => {
    const result = await axios.get(`${baseUrl}/reports/v2/groups/${groupId}/filters`);
    return result.data;
};

const applyGroupFilters = async (groupId: string, request: ApplyFiltersRequest): Promise<ApplyFiltersResponse> => {
    const result = await axios.post(`${baseUrl}/reports/v2/groups/${groupId}/filters`, request);
    return result.data;
};




/**
 * DASHBOARD & PINS
 */

const getGroupDashboard = async (groupId: string): Promise<ReportGroupDashboard> => {
    const result = await axios.get(`${baseUrl}/reports/v2/groups/${groupId}/dashboard`);
    return result.data;
};

const getGroupReportsWithSummaries = async (groupId: string): Promise<GroupReportWithSummaries[]> => {
    const result = await axios.get(`${baseUrl}/reports/v2/groups/${groupId}/reports`);
    return result.data;
};

const triggerDashboardRefresh = async (groupId: string): Promise<void> => {
    await axios.post(`${baseUrl}/reports/v2/groups/${groupId}/refresh`);
};

const executePinAction = async (groupId: string, action: string, payload?: any): Promise<any> => {
    const result = await axios.post(`${baseUrl}/reports/v2/groups/${groupId}/dashboard/pins/actions`, {
        action,
        ...payload
    });
    return result.data;
};

const getActiveRefreshesStatus = async (): Promise<{ refreshingGroups: string[] }> => {
    const result = await axios.get(`${baseUrl}/reports/v2/active-refreshes`);
    return result.data;
};


export {
    // Reports
    runReportV2,
    getReportResultsV2,
    getReportSummaryV2,
    getReportDefinition,
    getReportsMetadata,
    getReportStalenessStatus,
    exportReport,
    getUserReports,
    updateReportMetadata,
    checkReportStale,
    executeReportAction,
    getActiveRefreshesStatus,
    
    // Groups
    getAllReportGroups,
    newReportGroup,
    deleteReportGroup,
    renameReportGroup,
    assignReportsToGroup,
    executeGroupAction,
    
    // Dashboard
    getGroupDashboard,
    getGroupReportsWithSummaries,
    triggerDashboardRefresh,
    executePinAction,

    // Global Filters
    getGroupFilters,
    applyGroupFilters
};
